// vendors
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

// constants
import { APP_SECTIONS_PATHS } from 'constants/routes';

// components
import ClassroomDetailsList from './ClassroomDetailsList';
import Dialog from 'components/dialog/Dialog';
import RendererIf from 'components/RendererIf';
import ResultActionModal from 'components/shared/ResultActionModal/ResultActionModal';

// styles
import { ClassroomDetailsTab } from './styles';
import memberAddedErrorImage from 'images/error.svg';
import memberAddedSuccessImage from 'images/checked.svg';


// hooks
import { useRouter } from 'routes';
import useTeacher from 'hooks/useTeacher';
import { StylesImageModalResultsDeleteTeacher } from '../center/styles/TeacherList';

interface ITeacherProps {
  data: any;
  classroomName: string;
  refreshTeacherList?: Function;
  handleOnFinishDeleteTeacherFlow?: Function;
}

const ClassroomDetailsTeachers: React.FC<ITeacherProps> = ({
  data,
  classroomName,
  refreshTeacherList = () => {},
  handleOnFinishDeleteTeacherFlow = () => {},
}) => {
  const { deleteTeacher } = useTeacher();
  const { navigateTo, currentParamsRef } = useRouter();
  const { t: translate } = useTranslation();
  const [idTeacherToDelete, setIdTeacherToDelete] = useState<any>(false);
  const tableColumnHeaders = [
    {
      title: translate('settings:TEACHER_NAME'),
      withSortButton: true,
    },
    {
      title: translate('settings:CENTER_SECTION.POSITION'),
    },
    {
      title: translate('common:EMAIL'),
    },
  ];

  const {
    current: { centerId },
  } = currentParamsRef;


  const [showResultActionModal, setShowResultActionModal] = useState<any>(
    false,
  );

  const ConfigResultActionModal = {
    success: {
      title: translate('common:SUCCESS'),
      image: memberAddedSuccessImage,
      configPopsModal: {
        showCloseButton: false,
        onClosedModal: () => {
          if (setShowResultActionModal) setShowResultActionModal(false);
        },
      },
    },
    error: {
      title: translate('settings:ERROR'),
      image: memberAddedErrorImage,
      configPopsModal: {
        showCloseButton: true,
        onClosedModal: () => {
          if (setShowResultActionModal) setShowResultActionModal(false);
        },
      },
    },
  };

  const [statusResultActionModal, setStatusResultActionModal] = useState<any>(
    ConfigResultActionModal.error,
  );


  const onClickDeleteTeacher = (teacherId: number) => {
    setIdTeacherToDelete(teacherId);
  };

  const handleOnDeleteTeacher = () => {
    deleteTeacher(centerId, idTeacherToDelete)
      .then((response: any) => {
        setStatusResultActionModal(ConfigResultActionModal.success);
        if (handleOnFinishDeleteTeacherFlow) handleOnFinishDeleteTeacherFlow();
      })
      .catch(() => {
        setStatusResultActionModal(ConfigResultActionModal.error);
      })
      .finally(() => {
        setIdTeacherToDelete(false);
        setShowResultActionModal(true);
      });
  };

  const onClickTeacher = (teacherId: number) => {
    navigateTo(APP_SECTIONS_PATHS.TEACHER_PROFILE, {
      teacherId,
      className: classroomName,
      prev: 'classroom-details',
      origin: 'classroom',
    });
  };

  return (
    <>
      <Dialog
        mainText={translate('settings:DELETE')}
        cancelText={translate('common:CANCEL')}
        title={translate('settings:CENTER_SECTION.TEACHER_DELETE.MODAL_CONFIRM.TITLE')}
        show={idTeacherToDelete}
        onMainAction={handleOnDeleteTeacher}
        onDialogClose={() => setIdTeacherToDelete(false)}
        onCancelAction={() => setIdTeacherToDelete(false)}
      >
        <>{translate('settings:CENTER_SECTION.TEACHER_DELETE.MODAL_CONFIRM.TEXT')}</>
      </Dialog>
      <RendererIf condition={showResultActionModal}>
        <ResultActionModal
          styles={{ Image: StylesImageModalResultsDeleteTeacher }}
          {...statusResultActionModal}
        />
      </RendererIf>
      <ClassroomDetailsTab>
        <ClassroomDetailsList
          listData={data}
          onClickItem={onClickTeacher}
          onDeleteItem={onClickDeleteTeacher}
          emptyMessage={translate('settings:CENTER_SECTION.TEACHERS_EMPTY')}
          tableColumnHeaders={tableColumnHeaders}
        />
      </ClassroomDetailsTab>
    </>
  );
};

export default ClassroomDetailsTeachers;
